import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Chip,
  IconButton
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { jobAidService } from "../../services/api";

const JobAidPreview = ({ isModal = false, onClose, refreshOnClose, returnUrl,isLearnerMode = false }) => {
  const { id, courseId } = useParams(); 
  const navigate = useNavigate();

  const [jobAid, setJobAid] = useState(null);
  const [loading, setLoading] = useState(true);
  

  useEffect(() => {
    const fetchJobAid = async () => {
      try {
        setLoading(true);
        const res = await jobAidService.getById(id);
        setJobAid(res.data?.data);
      } catch (err) {
        console.error("Error loading job aid:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobAid();
  }, [id]);

  const handleClose = () => {
    if (refreshOnClose) {
      console.log("Calling refreshOnClose callback");
      refreshOnClose();
    }

    if (isModal && onClose) {
      onClose();
    } else {
      // Navigate back to course or fallback
      navigate(returnUrl || (courseId ? `/courses/${courseId}` : "/job-aids"));
    }
  };

  const isYouTubeUrl = (url) => url.includes("youtube.com") || url.includes("youtu.be");
  const isGoogleDriveUrl = (url) => url.includes("drive.google.com");

  const convertYouTubeToEmbed = (url) => {
    if (url.includes("watch?v=")) {
      const videoId = url.split("watch?v=")[1].split("&")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes("youtu.be")) {
      const videoId = url.split("/").pop();
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  const convertDriveToPreview = (url) => {
    const fileId = url.match(/[-\w]{25,}/);
    return fileId ? `https://drive.google.com/file/d/${fileId[0]}/preview` : url;
  };

  if (loading) {
    return (
      <Box height="100vh" display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!jobAid) {
    return (
      <Box p={4}>
        <Typography variant="h6" color="error">Job Aid not found.</Typography>
      </Box>
    );
  }

  const jobAids_url =
    jobAid.content_type === "url"
      ? jobAid.jobAids_url
      : jobAid.file_path || jobAid.path;

  if (!jobAids_url) {
    return (
      <Box p={4}>
        <Typography variant="h6" color="error">No file or URL found.</Typography>
      </Box>
    );
  }

  const finalUrl = (() => {
    if (isYouTubeUrl(jobAids_url)) return convertYouTubeToEmbed(jobAids_url);
    if (isGoogleDriveUrl(jobAids_url)) return convertDriveToPreview(jobAids_url);
    if (jobAids_url.startsWith("http") || jobAids_url.startsWith("/")) return jobAids_url;
    return `/uploads/job-aids/${jobAids_url}`;
  })();

  const isPdf = finalUrl.endsWith(".pdf");
  const isImage = finalUrl.match(/\.(jpg|jpeg|png|gif|svg)$/i);
  const isVideo = finalUrl.match(/\.(mp4|webm|ogg)$/i);

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          bgcolor: "#1a1a1a",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#fff",
              maxWidth: "100%",             
              overflowWrap: "break-word",
              wordBreak: "break-word",
              whiteSpace: "normal",        
              lineHeight: 1.4  
                      }}
                    >
            {jobAid.name}
          </Typography>

          {!isLearnerMode && (
            <Chip
              label="Preview Mode"
              size="small"
              color="primary"
              sx={{
                ml: 2,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "#fff",
                height: 24,
              }}
            />
          )}
        </Box>

        <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </Box>

      <Box flexGrow={1} overflow="hidden">
        {isPdf ? (
          <iframe
            src={finalUrl}
            title="Job Aid PDF"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : isImage ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            bgcolor="#f9f9f9"
          >
            <img
              src={finalUrl}
              alt={jobAid.name}
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
          </Box>
        ) : isVideo || isYouTubeUrl(finalUrl) || isGoogleDriveUrl(finalUrl) ? (
          <iframe
            src={finalUrl}
            title="Job Aid Video"
            width="100%"
            height="100%"
            allow="fullscreen"
            allowFullScreen
            style={{ border: "none" }}
          />
        ) : (
          <Box p={4}>
            <Typography variant="h6" color="error">Unsupported file format.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default JobAidPreview;
